import { useMemo, useState } from 'react';

import { format, getMonth, getYear, startOfDay, startOfMonth } from 'date-fns';

import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useGetTrainingsForAnalytic } from '../../../../redux/api/trainingsAPI';
import { TrainingStatus } from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';
import { calculateTrainingTime } from '../../../../utils/datetime/date-time-utils';

export function useStatisticsCalendar() {
  const { navigate } = useNavigationStack();
  const [selectedMonth, setSelectedMonth] = useState<Date>(
    startOfMonth(new Date()),
  );
  const queryParams = useMemo(
    () => ({
      month: getMonth(selectedMonth) + 1,
      year: getYear(selectedMonth),
    }),
    [selectedMonth],
  );
  const {
    data: trainings,
    isLoading,
    refetch,
  } = useGetTrainingsForAnalytic(queryParams);

  const finishedTrainingDates = useMemo(() => {
    if (!trainings) return [];
    return trainings
      .filter(
        (training) =>
          training.startDate && training.status === TrainingStatus.finished,
      )
      .map((training) => startOfDay(new Date(training.startDate)).getTime());
  }, [trainings]);

  const startedTrainingDates = useMemo(() => {
    if (!trainings) return [];
    return trainings
      .filter(
        (training) =>
          training.startDate && training.status === TrainingStatus.started,
      )
      .map((training) => startOfDay(new Date(training.startDate)).getTime());
  }, [trainings]);

  const planedTrainingDates = useMemo(() => {
    if (!trainings) return [];
    return trainings
      .filter(
        (training) =>
          training.planedStartDate &&
          training.status === TrainingStatus.notStarted,
      )
      .map((training) =>
        startOfDay(new Date(training.planedStartDate)).getTime(),
      );
  }, [trainings]);

  const handleDateChange = (newDate: Date | null) => {
    if (!newDate) return;

    const selectedDay = startOfDay(newDate).getTime();
    const hasTraining =
      finishedTrainingDates.includes(selectedDay) ||
      startedTrainingDates.includes(selectedDay);
    if (!hasTraining) {
      return;
    }

    const dateStr = format(startOfDay(newDate), 'yyyy-MM-dd');
    navigate(Paths.statistics.toDay(dateStr));
  };

  const handleMonthChange = (newMonth: Date) => {
    setSelectedMonth(startOfMonth(newMonth));
  };

  const monthStatistics = useMemo(() => {
    if (!trainings) {
      return {
        count: 0,
        totalLoad: 0,
        averageLoad: 0,
        totalCalories: 0,
        averageCalories: 0,
        totalTime: 0,
      };
    }

    const count = trainings.length;
    const totalLoad = trainings.reduce(
      (sum, training) => sum + (training.totalLoad || 0),
      0,
    );
    const averageLoad = count > 0 ? totalLoad / count : 0;
    const totalCalories = trainings.reduce(
      (sum, training) => sum + (training.totalCalories || 0),
      0,
    );
    const averageCalories = count > 0 ? totalCalories / count : 0;
    const totalTime = trainings.reduce((sum, training) => {
      return sum + calculateTrainingTime(training);
    }, 0);

    return {
      count,
      totalLoad,
      averageLoad,
      totalCalories,
      averageCalories,
      totalTime,
    };
  }, [trainings]);

  return {
    finishedTrainingDates,
    startedTrainingDates,
    planedTrainingDates,
    handleDateChange,
    selectedMonth,
    handleMonthChange,
    monthStatistics,
    isLoading,
    refetch,
  };
}
